import React, { useEffect, useMemo, useState } from 'react';
import SidebarToggleHandler from '../../../../../@coremat/CmtLayouts/Vertical/SidebarToggleHandler';
import Toolbar from '@mui/material/Toolbar';
import { Box, Typography, IconButton, Tooltip } from '@mui/material';
import { alpha } from '@mui/material/styles';
import makeStyles from '@mui/styles/makeStyles';
import { useTheme } from '@emotion/react';
import DarkModeIcon from '@mui/icons-material/DarkMode';
import LightModeIcon from '@mui/icons-material/LightMode';
import { useTranslation } from 'react-i18next';

import Logo from '../Logo';
import CartButton from '../CartButton/CartButton';
import AccountButton from '../AccountButton/AccountButton';
import { useLocation } from 'react-router';
import { NavLink } from 'react-router-dom';
import { useSelector } from 'react-redux';
import QuicklinksButton from '../QuickLinksButton/QuickLinksButton';
import ExpandableSearch from './ExpandableSearch';
import ContactButton from '../SupportButton/SupportButton';
import { IsImpersonationMode, IsLocalDevOrServerDev, IsProduction } from 'config/config';
import LanguageSwitcher from '../LanguageSwitcher/LanguageSwitcher';
import LogoutButton from '../LogoutButton/LogoutButton';
import { homePage } from 'config/config';
import SiteNav from '@acnb/CustomComponents/SiteNav/SiteNav';
import MegaSiteNav from '@acnb/CustomComponents/MegaSiteNav/MegaSiteNav';
import {
  doesSectionUseMegaMenu,
  getDealerPortalSectionName,
  getDealerPortalSectionValue,
  DealerPortalSectionEnumValue,
} from 'enums/DealerPortalSectionEnum';
import { useDealerPortalMenuTree } from 'services/Data/DealerPortalMenuServices';
import { buildMenuItems } from 'services/Utilities/menuUtils';

const useStyles = makeStyles(theme => ({
  root: {
    width: '100%',
    paddingLeft: 16,
    paddingRight: 16,
    minHeight: 64,
    [theme.breakpoints.up('md')]: {
      minHeight: 72,
    },
    [theme.breakpoints.up('md')]: {
      paddingLeft: 24,
      paddingRight: 24,
    },
  },
  searchRoot: {
    position: 'relative',
    width: 260,
    [theme.breakpoints.up('md')]: {
      width: 350,
    },
    '& .MuiSvgIcon-root': {
      position: 'absolute',
      left: 18,
      top: '50%',
      transform: 'translateY(-50%)',
      zIndex: 1,
    },
    '& .MuiInputBase-root': {
      width: '100%',
    },
    '& .MuiInputBase-input': {
      height: 48,
      borderRadius: 30,
      backgroundColor: alpha(theme.palette.common.dark, 0.38),
      color: alpha(theme.palette.common.white, 0.7),
      boxSizing: 'border-box',
      padding: '5px 15px 5px 50px',
      transition: 'all 0.3s ease',
      '&:focus': {
        backgroundColor: alpha(theme.palette.common.dark, 0.58),
        color: alpha(theme.palette.common.white, 0.7),
      },
    },
  },
  langRoot: {
    borderLeft: `solid 1px ${alpha(theme.palette.common.dark, 0.15)}`,
    minHeight: 72,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    paddingLeft: 8,
    paddingRight: 8,
    position: 'relative',
    [theme.breakpoints.down('md')]: {
      minHeight: 64,
    },
  },
  iconBtn: {
    color: alpha(theme.palette.common.white, 0.38),
    '&:hover, &:focus': {
      color: theme.palette.common.white,
    },
  },
  navContainer: {
    "& div[class^='src__GridItem-sc'] a": {
      width: '100%!important',
      height: '100%!important',
      alignContent: 'space-around',
      textAlign: 'center',
    },
  },
}));

const Header = () => {
  const classes = useStyles();
  const [certThemeMode, setCertThemeMode] = useState('light');
  const location = useLocation();
  const { t, i18n } = useTranslation();
  const accountReducer = useSelector(state => state.AccountReducer);
  const isLoggedIn = Boolean(accountReducer?.isLoggedIn);

  const theme = useTheme();

  const showSiteNav = useMemo(
    () =>
      !(
        location.pathname === '/dealer' ||
        location.pathname === '/dealer/home-page' ||
        location.pathname.startsWith('/dealer/profile') ||
        location.pathname.startsWith('/dealer/faq') ||
        location.pathname.startsWith('/dealer/home-page/merchandising-program') ||
        location.pathname.startsWith('/dealer/home-page/exterior-signage')
      ),
    [location.pathname],
  );

  const currentSection = useMemo(() => {
    const pathnameSegments = location.pathname.split('/').filter(Boolean);
    const sectionSegmentIndex = pathnameSegments.findIndex(segment => segment.toLowerCase() === 'section');
    const sectionFromPath = getDealerPortalSectionValue(pathnameSegments[sectionSegmentIndex + 1]);

    if (sectionFromPath > DealerPortalSectionEnumValue.None) {
      return sectionFromPath;
    }

    if (location.pathname.startsWith('/dealer/useful-links')) {
      return DealerPortalSectionEnumValue.UsefulLinks;
    }

    if (location.pathname.startsWith('/dealer/home-page/exterior-signage')) {
      return DealerPortalSectionEnumValue.ExteriorSignage;
    }

    if (location.pathname.startsWith('/dealer/home-page/merchandising-program')) {
      return DealerPortalSectionEnumValue.MerchandisingServices;
    }

    return DealerPortalSectionEnumValue.DisplaysToolingAndFurniture;
  }, [location.pathname]);

  const currentSectionName = useMemo(() => getDealerPortalSectionName(currentSection) || 'DisplaysToolingAndFurniture', [
    currentSection,
  ]);
  const isMegaMenuSection = useMemo(() => doesSectionUseMegaMenu(currentSection), [currentSection]);

  const { data: dealerPortalMenuTreeData } = useDealerPortalMenuTree(
    currentSection,
    isLoggedIn && showSiteNav && !location.pathname.startsWith('/dealer/certification'),
  );

  useEffect(() => {
    const syncCertTheme = () => {
      if (typeof window === 'undefined') return;
      const stored = window.localStorage.getItem('certificationThemeMode');
      setCertThemeMode(stored === 'dark' ? 'dark' : 'light');
    };

    syncCertTheme();

    window.addEventListener('cert-theme-mode-changed', syncCertTheme);
    window.addEventListener('storage', syncCertTheme);
    return () => {
      window.removeEventListener('cert-theme-mode-changed', syncCertTheme);
      window.removeEventListener('storage', syncCertTheme);
    };
  }, []);

  const menuItems = useMemo(
    () =>
      buildMenuItems({
        pathname: location.pathname,
        t,
        dealerPortalMenuTreeData,
        language: i18n.language,
        currentSectionName,
      }),
    [location.pathname, t, dealerPortalMenuTreeData, i18n.language, currentSectionName],
  );

  return (
    <>
      <Toolbar className={classes.root}>
        <SidebarToggleHandler edge="start" color="inherit" aria-label="menu" />
        <Logo ml={2} color="white" />
        <Box flex={3}>
          <NavLink to={homePage}>
            <Typography
              variant="h1"
              sx={{
                color: theme.palette.common.white,
                backgroundColor: theme.palette.primary.main,
                marginLeft: 5,
                fontSize: { xs: 14, md: 21 },
              }}>
              {t('applicationHeaderName')}
            </Typography>
          </NavLink>
        </Box>
        <Box flex={1} />
        <Box className={classes.langRoot}>
          <ExpandableSearch />
          <QuicklinksButton />
          <ContactButton />
          <AccountButton />
          <CartButton />
          {location.pathname.startsWith('/dealer/certification') && (
            <Tooltip title={certThemeMode === 'dark' ? 'Light mode' : 'Dark mode'}>
              <IconButton
                size="small"
                onClick={() => window.dispatchEvent(new Event('cert-theme-toggle-request'))}
                sx={{
                  color: theme.palette.common.white,
                  ml: 1,
                  mr: 1,
                  border: `1px solid ${alpha(theme.palette.common.white, 0.2)}`,
                  '&:hover': {
                    bgcolor: alpha(theme.palette.common.white, 0.08),
                  },
                }}>
                {certThemeMode === 'dark' ? <LightModeIcon /> : <DarkModeIcon />}
              </IconButton>
            </Tooltip>
          )}

          {(IsLocalDevOrServerDev || (IsImpersonationMode && IsProduction)) && <LanguageSwitcher />}
          {IsLocalDevOrServerDev && <LogoutButton />}
        </Box>
      </Toolbar>
      {showSiteNav && (
        <div className={classes.navContainer}>
          {isMegaMenuSection ? (
            <MegaSiteNav
              items={menuItems}
              align="center"
              rowHeight={50}
              background={theme.palette.primary.dark}
              color={theme.palette.primary.contrastText}
              contentBackground={theme.palette.primary.dark}
              contentColor={theme.palette.primary.contrastText}
              breakpoint={768}
              style={{ zIndex: 99 }}
            />
          ) : (
            <SiteNav
              items={menuItems}
              align="center"
              rowHeight={50}
              background={theme.palette.primary.dark}
              color={theme.palette.primary.contrastText}
              contentBackground={theme.palette.primary.dark}
              contentColor={theme.palette.primary.contrastText}
              breakpoint={768}
              style={{ zIndex: 99 }}
            />
          )}
        </div>
      )}
    </>
  );
};

export default Header;
